import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getDeviceByID, deleteDevice, updateDeviceByID, getDevices } from '@reducers/device';
import { changeAccess } from '@reducers/membership';
import { navigateBack, Alert, localTime } from '@helpers';
import type { RootState } from '@store/types';

export const useManageDeviceDetails = (id: string) => {
  const dispatch = useAppDispatch();
  const devices = useSelector((state: RootState) => state.device.devices);

  const currentDevice = devices.find((d: any) => d.deviceId === id);
  const [deviceData, setDevice] = useState<any>(currentDevice || {});
  const [deleting, setDeleting] = useState(false);
  const [changingAccess, setChangingAccess] = useState(false);
  const [showEditDeviceName, setShowEditDeviceName] = useState(false);

  useEffect(() => {
    if (!deviceData.deviceId) {
      (async () => {
        const d = await dispatch(getDeviceByID(id));
        if (!d?.devices?.[0]) {
          navigateBack();
          return;
        }
        setDevice(d.devices[0]);
      })();
    }
  }, []);

  const toggleEditDeviceName = useCallback(() => {
    setShowEditDeviceName((prev) => !prev);
  }, []);

  const changeDeviceAccess = useCallback(async () => {
    if (changingAccess) return;
    setChangingAccess(true);
    await dispatch(
      changeAccess({
        identifierType: 'Device',
        identifier: deviceData.deviceId,
        blocked: !deviceData.blocked,
      }),
    );
    setDevice((prev: any) => ({ ...prev, blocked: !prev.blocked }));
    setChangingAccess(false);
  }, [dispatch, deviceData.deviceId, deviceData.blocked, changingAccess]);

  const deleteDeviceFN = useCallback(async () => {
    setDeleting(true);
    const result = await dispatch(deleteDevice(id));
    if (result?.isSuccess) {
      navigateBack();
    } else {
      Alert({ title: 'Error', text: result?.errorMessage || i18n.t('Unknown error') });
    }
    setDeleting(false);
  }, [dispatch, id]);

  const triggerDelete = useCallback(() => {
    Alert({
      title: i18n.t('Are you sure?'),
      actions: [
        { text: i18n.t('No'), onPress: () => {}, style: 'primary' },
        { text: i18n.t('Yes'), onPress: deleteDeviceFN },
      ],
    });
  }, [deleteDeviceFN]);

  const onSubmitEditName = useCallback(
    async (data: any, onClose: () => void) => {
      const newData = { ...deviceData, ...data };
      await dispatch(
        updateDeviceByID(newData, async () => {
          await dispatch(getDevices({ reset: true, orderBy: { LastUsedOn: 'Desc' } }));
          setDevice(newData);
          onClose();
        }),
      );
    },
    [dispatch, deviceData],
  );

  const device = {
    name: { value: deviceData.name, name: i18n.t('Name'), edit: true, onClick: toggleEditDeviceName },
    type: { value: deviceData.deviceType, name: i18n.t('Type'), noArrow: true },
    make: { value: deviceData.make, name: i18n.t('Make'), noArrow: true },
    model: { value: deviceData.model, name: i18n.t('Model'), noArrow: true },
    platform: { value: deviceData.platform, name: i18n.t('Platform'), noArrow: true },
    appVersion: { value: deviceData.appVersion, name: i18n.t('App version'), noArrow: true },
    lastUsedOn: { value: localTime(deviceData.lastUsedOn).format('DD MMM YYYY [at] HH:mm'), name: i18n.t('Last used'), noArrow: true },
    lastLocation: {
      value: `${deviceData.lastLocation ? deviceData.lastLocation : ''} ${deviceData.ipV4 ? `(${deviceData.ipV4})` : ''}`,
      name: i18n.t('Last location'),
      noArrow: true,
    },
    registeredOn: { value: localTime(deviceData.registeredOn).format('DD MMM YYYY [at] HH:mm'), name: i18n.t('Registered on'), noArrow: true },
    registeredBy: { value: deviceData.registeredBy, name: i18n.t('Registered by'), noArrow: true, },
  };

  return {
    deviceData,
    device,
    deleting,
    changingAccess,
    changeDeviceAccess,
    triggerDelete,
    showEditDeviceName,
    toggleEditDeviceName,
    onSubmitEditName,
  };
};
